import React, { useState, useEffect, useRef } from 'react';
import {
    Modal,
    Button,
    Space,
    Input,
    Typography,
    Divider,
    Alert,
    Spin,
    message,
    Select,
    Tooltip
} from 'antd';
import {
    PlusOutlined,
    CodeOutlined,
    LoadingOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import FilterPill from './FilterPill';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * CustomFieldsManager Component
 *
 * Allows users to define, test, and manage custom fields based on JavaScript expressions
 * that are evaluated against call records.
 */
const CustomFieldsManager = ({
                                 isVisible,
                                 onClose,
                                 customFields = [],
                                 onSave,
                                 sampleRecord = null,
                                 calls = [],
                                 prepopulatedExpression = ''
                             }) => {
    // Main state
    const [editMode, setEditMode] = useState(null); // null, 'add', or { id: 'some-id' }
    const [fields, setFields] = useState([]);

    // Field editor state
    const [expression, setExpression] = useState('record.');
    const [fieldName, setFieldName] = useState('');
    const [fieldDescription, setFieldDescription] = useState('');
    const [userHasTyped, setUserHasTyped] = useState(false);

    // Sample record state
    const [selectedCall, setSelectedCall] = useState(null);

    // Autocomplete state
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [highlightedSuggestion, setHighlightedSuggestion] = useState(0);
    const inputRef = useRef(null);

    // Evaluation state
    const [evaluationResult, setEvaluationResult] = useState({ value: null, error: null });
    const [isEvaluating, setIsEvaluating] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Reset and initialize when the modal becomes visible
    useEffect(() => {
        if (isVisible) {
            // Copy fields to avoid reference issues
            setFields(JSON.parse(JSON.stringify(customFields || [])));
            setEditMode(null);
            setHasChanges(false);
            setUserHasTyped(false);

            // Set initial sample record
            if (calls && calls.length > 0) {
                setSelectedCall(sampleRecord || calls[0]);
            }

            // Handle prepopulated expression if provided
            if (prepopulatedExpression) {
                // Delay to ensure modal is fully visible
                setTimeout(() => {
                    handleAdd(prepopulatedExpression);
                    setUserHasTyped(true);
                }, 100);
            }
        }
    }, [isVisible, customFields, sampleRecord, calls, prepopulatedExpression]);

    // Generate default field name
    const generateDefaultFieldName = () => {
        const existingNumbers = fields
            .map(f => {
                const match = f.name.match(/^CustomField_(\d+)$/);
                return match ? parseInt(match[1], 10) : 0;
            })
            .filter(n => n > 0);

        const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
        return `CustomField_${nextNumber}`;
    };

    // Start adding a new field
    const handleAdd = (initialExpression = 'record.') => {
        // Create a default new field
        const newField = {
            id: `custom-${Date.now()}`,
            name: generateDefaultFieldName(),
            description: '',
            expression: initialExpression,
            type: 'unknown'
        };

        // Add it to fields
        setFields(prevFields => [...prevFields, newField]);

        // Set up the editor state
        setExpression(initialExpression);
        setFieldName(newField.name);
        setFieldDescription('');
        setEvaluationResult({ value: null, error: null });
        setUserHasTyped(false);
        setHasChanges(true);

        // Set edit mode last to prevent race conditions
        setEditMode({ id: newField.id });
    };

    // Edit an existing field
    const handleEdit = (field) => {
        if (!field) return;

        // Set up the edit state
        setExpression(field.expression || 'record.');
        setFieldName(field.name || '');
        setFieldDescription(field.description || '');
        setEvaluationResult({ value: null, error: null });
        setUserHasTyped(true); // Assume user has typed when editing existing field

        // Set edit mode last to prevent race conditions
        setEditMode({ id: field.id });

        // Evaluate after a brief delay to ensure state is updated
        setTimeout(() => {
            if (selectedCall) {
                evaluateExpression(field.expression);
            }
        }, 50);
    };

    // Cancel editing
    const handleCancel = () => {
        setEditMode(null);
        setExpression('record.');
        setFieldName('');
        setFieldDescription('');
        setEvaluationResult({ value: null, error: null });
        setUserHasTyped(false);
    };

    // Remove a field
    const handleRemove = (fieldToRemove) => {
        if (!fieldToRemove) return;

        setFields(prevFields => prevFields.filter(field => field.id !== fieldToRemove.id));
        setHasChanges(true);

        // If we're currently editing this field, cancel editing
        if (editMode && typeof editMode === 'object' && editMode.id === fieldToRemove.id) {
            handleCancel();
        }
    };

    // Update the current field being edited
    const updateCurrentField = () => {
        if (!editMode || typeof editMode !== 'object' || !editMode.id) return;

        if (!fieldName.trim()) {
            message.error('Field name is required');
            return;
        }

        if (!expression.trim() || expression === 'record.') {
            message.error('Expression is required');
            return;
        }

        try {
            // Try to evaluate the expression
            const result = evaluateExpressionSync(expression);

            // Update the field
            setFields(prevFields =>
                prevFields.map(f => f.id === editMode.id ? {
                    ...f,
                    name: fieldName.trim(),
                    description: fieldDescription.trim(),
                    expression: expression.trim(),
                    type: typeof result.value
                } : f)
            );

            setHasChanges(true);
        } catch (error) {
            message.error(`Cannot update field: ${error.message}`);
        }
    };

    // Auto-evaluate expressions as user types
    useEffect(() => {
        if (!editMode || !selectedCall || !expression || !userHasTyped) return;

        const timer = setTimeout(() => {
            evaluateExpression(expression);
        }, 300);

        return () => clearTimeout(timer);
    }, [expression, selectedCall, editMode, userHasTyped]);

    // Auto-update field as user edits
    useEffect(() => {
        if (!editMode || !editMode.id) return;

        const timer = setTimeout(() => {
            updateCurrentField();
        }, 500);

        return () => clearTimeout(timer);
    }, [fieldName, fieldDescription, expression, userHasTyped]);

    // Evaluate an expression
    const evaluateExpression = (expr) => {
        if (!selectedCall) {
            setEvaluationResult({
                value: null,
                error: 'No sample record available'
            });
            return;
        }

        setIsEvaluating(true);

        try {
            const result = evaluateExpressionSync(expr);
            setEvaluationResult(result);
        } catch (error) {
            setEvaluationResult({
                value: null,
                error: error.message
            });
        } finally {
            setIsEvaluating(false);
        }
    };

    // Synchronous evaluation for immediate use
    const evaluateExpressionSync = (expr) => {
        if (!selectedCall) {
            throw new Error('No sample record available');
        }

        try {
            const evalFn = new Function('record', 'records', `return ${expr}`);
            const value = evalFn(selectedCall, calls);

            return {
                value,
                error: null
            };
        } catch (error) {
            throw error;
        }
    };

    // Format the result value for display
    const formatResultValue = (value) => {
        if (value === null) return 'null';
        if (value === undefined) return 'undefined';

        if (typeof value === 'object') {
            try {
                return JSON.stringify(value, null, 2);
            } catch (e) {
                return String(value);
            }
        }

        if (typeof value === 'string') {
            return `"${value}"`;
        }

        return String(value);
    };

    // Save all fields and close modal
    const handleSaveAll = () => {
        onSave(fields);
        onClose();
    };

    // Update autocomplete suggestions when cursor position or expression changes
    useEffect(() => {
        if (!editMode || !selectedCall || !expression.includes('record.')) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const textBeforeCursor = expression.substring(0, cursorPosition);
        const match = textBeforeCursor.match(/record\.([a-zA-Z0-9_.]*)$/);

        if (!match) {
            setShowSuggestions(false);
            return;
        }

        const prefix = match[1] || '';
        setHighlightedSuggestion(0);

        // Get paths from the selected call
        const getPaths = (obj, currentPath = '') => {
            if (!obj || typeof obj !== 'object') return [];

            let paths = [];
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    const newPath = currentPath ? `${currentPath}.${key}` : key;

                    paths.push({
                        path: newPath,
                        type: typeof obj[key],
                        value: obj[key]
                    });

                    if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
                        paths = [...paths, ...getPaths(obj[key], newPath)];
                    }
                }
            }
            return paths;
        };

        const allPaths = getPaths(selectedCall);

        const filteredSuggestions = allPaths
            .filter(item => item.path.startsWith(prefix))
            .map(item => {
                const displayPath = item.path;
                let displayValue = '';

                if (item.type === 'string') {
                    displayValue = `"${String(item.value).substring(0, 20)}${String(item.value).length > 20 ? '...' : ''}"`;
                } else if (item.type === 'number' || item.type === 'boolean') {
                    displayValue = String(item.value);
                } else if (item.type === 'object') {
                    displayValue = Array.isArray(item.value) ? 'Array' : 'Object';
                } else if (item.value === null) {
                    displayValue = 'null';
                } else if (item.value === undefined) {
                    displayValue = 'undefined';
                }

                return {
                    path: displayPath,
                    fullPath: `record.${displayPath}`,
                    type: item.type,
                    displayValue
                };
            });

        setSuggestions(filteredSuggestions);
        setShowSuggestions(filteredSuggestions.length > 0);
    }, [expression, cursorPosition, selectedCall, editMode]);

    // Handle expression input changes
    const handleExpressionChange = (e) => {
        setExpression(e.target.value);
        setCursorPosition(e.target.selectionStart || 0);
        if (!userHasTyped) {
            setUserHasTyped(true);
        }
    };

    // Track cursor position
    const handleCursorPositionChange = (e) => {
        setCursorPosition(e.target.selectionStart || 0);
    };

    // Change the sample record
    const handleCallChange = (callId) => {
        const call = calls.find(call => call.id === callId);
        if (call) {
            setSelectedCall(call);

            // Re-evaluate expression with the new selected call
            if (editMode && expression && userHasTyped) {
                evaluateExpression(expression);
            }
        }
    };

    // Handle selecting a suggestion
    const handleSuggestionSelect = (fullPath) => {
        const textBeforeCursor = expression.substring(0, cursorPosition);
        const match = textBeforeCursor.match(/record\.([a-zA-Z0-9_.]*)$/);

        if (!match) return;

        // Calculate positions for replacement
        const matchStart = textBeforeCursor.lastIndexOf('record.');
        const newExpression =
            expression.substring(0, matchStart) +
            fullPath +
            expression.substring(cursorPosition);

        // Update expression
        setExpression(newExpression);
        setShowSuggestions(false);
        setUserHasTyped(true);

        // Calculate new cursor position
        const newPosition = matchStart + fullPath.length;

        // Set cursor position after a brief delay
        setTimeout(() => {
            if (!inputRef.current) return;

            try {
                const textarea = inputRef.current.resizableTextArea.textArea;
                textarea.focus();
                textarea.setSelectionRange(newPosition, newPosition);
                setCursorPosition(newPosition);
            } catch (err) {
                console.error('Error setting selection range:', err);
            }
        }, 10);
    };

    // Check if fields have changed compared to customFields
    const hasFieldsChanged = () => {
        if (customFields.length !== fields.length) return true;

        // Simple deep comparison of key properties
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            const originalField = customFields.find(f => f.id === field.id);

            if (!originalField) return true;

            if (
                field.name !== originalField.name ||
                field.description !== originalField.description ||
                field.expression !== originalField.expression
            ) {
                return true;
            }
        }

        return false;
    };

    // Find the current field being edited
    const getCurrentField = () => {
        if (!editMode || typeof editMode !== 'object' || !editMode.id) return null;
        return fields.find(f => f.id === editMode.id);
    };

    // Render Fields List
    const renderFieldsList = () => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {fields.map(field => (
                <FilterPill
                    key={field.id}
                    label={field.name}
                    active={editMode && typeof editMode === 'object' && editMode.id === field.id}
                    onClick={() => handleEdit(field)}
                    onRemove={() => handleRemove(field)}
                    style={{
                        backgroundColor: '#10b981',
                        color: 'white'
                    }}
                />
            ))}
            <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={() => handleAdd()}
                style={{
                    borderRadius: '16px',
                    height: '32px',
                    padding: '4px 12px',
                    marginLeft: 4
                }}
            >
                Add field
            </Button>
        </div>
    );

    // Render Field Editor
    const renderFieldEditor = () => {
        const currentField = getCurrentField();
        if (!currentField) return null;

        return (
            <div style={{ marginTop: 8, marginBottom: 16 }}>
                <Divider style={{ margin: '0 0 16px 0' }} />
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                        <Text strong>Field Name:</Text>
                        <Input
                            value={fieldName}
                            onChange={(e) => setFieldName(e.target.value)}
                            placeholder="Enter field name"
                            style={{ marginTop: 8 }}
                        />
                    </div>

                    <div>
                        <Text strong>Description (optional):</Text>
                        <Input
                            value={fieldDescription}
                            onChange={(e) => setFieldDescription(e.target.value)}
                            placeholder="Enter description"
                            style={{ marginTop: 8 }}
                        />
                    </div>

                    <div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Text strong>Expression:</Text>
                            <Tooltip title="Define a JavaScript expression that will be evaluated against each record. Use 'record' to access the current record and 'records' to access all records.">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                            </Tooltip>
                        </div>

                        <div style={{ position: 'relative' }}>
                            <TextArea
                                ref={inputRef}
                                value={expression}
                                onChange={handleExpressionChange}
                                onKeyUp={handleCursorPositionChange}
                                onClick={handleCursorPositionChange}
                                onKeyDown={(e) => {
                                    if (showSuggestions && suggestions.length > 0) {
                                        // Tab or Enter to autocomplete
                                        if ((e.key === 'Tab' || e.key === 'Enter') &&
                                            e.target.selectionStart === e.target.selectionEnd) {
                                            e.preventDefault();
                                            handleSuggestionSelect(suggestions[highlightedSuggestion].fullPath);
                                            return;
                                        }

                                        // Arrow down
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            setHighlightedSuggestion((prev) =>
                                                prev < suggestions.length - 1 ? prev + 1 : prev
                                            );
                                            return;
                                        }

                                        // Arrow up
                                        if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            setHighlightedSuggestion((prev) => prev > 0 ? prev - 1 : 0);
                                            return;
                                        }

                                        // Escape
                                        if (e.key === 'Escape') {
                                            e.preventDefault();
                                            setShowSuggestions(false);
                                            return;
                                        }
                                    }
                                }}
                                placeholder="Enter expression, e.g.: record.profScore * 0.7 + record.protScore * 0.3"
                                autoSize={{ minRows: 3, maxRows: 6 }}
                                style={{
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                    marginTop: 8
                                }}
                            />

                            {showSuggestions && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        zIndex: 1000,
                                        background: 'white',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '2px',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                        maxHeight: '200px',
                                        overflow: 'auto',
                                        width: '100%'
                                    }}
                                >
                                    {suggestions.map((suggestion, index) => (
                                        <div
                                            key={index}
                                            onClick={() => handleSuggestionSelect(suggestion.fullPath)}
                                            style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                borderBottom: index === suggestions.length - 1 ? 'none' : '1px solid #f0f0f0',
                                                background: highlightedSuggestion === index
                                                    ? '#e6f7ff'
                                                    : index % 2 === 0 ? '#fafafa' : 'white',
                                                borderLeft: highlightedSuggestion === index ? '3px solid #1890ff' : 'none',
                                            }}
                                            onMouseEnter={() => setHighlightedSuggestion(index)}
                                        >
                                            <span style={{
                                                fontWeight: highlightedSuggestion === index ? 'bold' : 'normal',
                                                paddingLeft: highlightedSuggestion === index ? '0' : '3px',
                                            }}>
                                                {suggestion.path}
                                            </span>
                                            <span style={{
                                                color: '#888',
                                                marginLeft: 16,
                                                fontStyle: 'italic',
                                                fontSize: '12px'
                                            }}>
                                                {suggestion.displayValue} <Text type="secondary">({suggestion.type})</Text>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Evaluation results */}
                        {isEvaluating ? (
                            <div style={{ marginTop: 8, padding: 8, background: '#f9f9f9', borderRadius: 4 }}>
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                                <Text style={{ marginLeft: 8 }}>Evaluating expression...</Text>
                            </div>
                        ) : (
                            userHasTyped && (evaluationResult.error || evaluationResult.value !== null) ? (
                                <div style={{ marginTop: 8, padding: 8, background: '#f9f9f9', borderRadius: 4, border: '1px solid #d9d9d9' }}>
                                    {evaluationResult.error ? (
                                        <div style={{ color: '#ff4d4f' }}>
                                            <Text strong style={{ color: '#ff4d4f' }}>Error: </Text>
                                            <Text style={{ color: '#ff4d4f' }}>{evaluationResult.error}</Text>
                                        </div>
                                    ) : (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <Text strong style={{ marginRight: 8 }}>Result: </Text>
                                                <div style={{
                                                    fontFamily: 'monospace',
                                                    wordBreak: 'break-word',
                                                    maxHeight: '60px',
                                                    overflow: 'auto'
                                                }}>
                                                    {formatResultValue(evaluationResult.value)}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
                                                <Text strong>Type: </Text>
                                                <Text code style={{ marginLeft: 8 }}>
                                                    {typeof evaluationResult.value}
                                                </Text>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : null
                        )}
                    </div>
                </Space>
            </div>
        );
    };

    // Render the sample data display section
    const renderSampleData = () => {
        if (!editMode) return null;

        return (
            <div style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <Text strong>Sample Record</Text>
                    <Select
                        showSearch
                        style={{ width: 240 }}
                        value={selectedCall?.id}
                        onChange={handleCallChange}
                        placeholder="Select record"
                    >
                        {calls.map(call => (
                            <Option key={call.id} value={call.id}>
                                {call.agentName || call.id}
                            </Option>
                        ))}
                    </Select>
                </div>

                {selectedCall ? (
                    <div style={{
                        maxHeight: 200,
                        overflow: 'auto',
                        border: '1px solid #f0f0f0',
                        borderRadius: 4,
                        padding: 12,
                        background: '#fafafa',
                        marginBottom: 16
                    }}>
                        <pre style={{ fontSize: 12, margin: 0 }}>
                            {JSON.stringify(selectedCall, null, 2)}
                        </pre>
                    </div>
                ) : (
                    <Alert
                        message="No sample record available"
                        description="Please ensure there are call records available."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                {editMode && typeof editMode === 'object' && (
                    <Button
                        type="link"
                        danger
                        onClick={() => handleRemove(getCurrentField())}
                        style={{ paddingLeft: 0 }}
                    >
                        Remove field
                    </Button>
                )}
            </div>
        );
    };
    //console.log("Rendering CustomFieldsManager");
    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CodeOutlined style={{ marginRight: 8 }} />
                    <span>Custom Fields Manager</span>
                </div>
            }
            open={isVisible}
            onCancel={onClose}
            width={800}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    onClick={handleSaveAll}
                    disabled={!hasChanges && !hasFieldsChanged()}
                >
                    Apply
                </Button>
            ]}
        >
            {/* Intro text */}
            <Paragraph style={{ marginBottom: 16 }}>
                Create custom fields to derive new data points from your call records. These fields will be calculated for each record and can be used throughout the analytics dashboard like any other field.
            </Paragraph>

            {/* Fields list */}
            {renderFieldsList()}

            {/* Field editor - only shows when in edit mode */}
            {editMode && renderFieldEditor()}

            {/* Sample data - only shows when in edit mode */}
            {editMode && renderSampleData()}

            {/* Show info when no fields exist */}
            {fields.length === 0 && !editMode && (
                <Alert
                    message="No custom fields yet"
                    description="Click 'Add field' to create your first custom field."
                    type="info"
                    showIcon
                />
            )}
        </Modal>
    );
};

export default CustomFieldsManager;
